import { useEffect, useRef, useState } from 'react';
import { hasPermission } from '@/types';
import { Link } from 'react-router-dom';
import { bank as api } from '@/api/endpoints';
import type { BankMeta, BankTransaction, BankTxMatchStatus } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { useAuth } from '@/auth/AuthContext';
import { validateUploadSize, type UploadProgress } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';
import { formatCurrency } from '@/lib/money';

/** Dokładna data i godzina (do minuty) po polsku, np. „10.07.2026, 14:32". */
const fmtDateTime = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Bank / rozliczenia (Finanse, V84): import wyciągu (CSV/MT940), lista transakcji z automatycznym
 * dopasowaniem do faktur i potwierdzaniem/odrzucaniem sugestii (rekoncyliacja). Docelowo źródłem
 * będzie też API banku (Erste/PSD2).
 */
export function BankPage() {
  const { user } = useAuth();
  const canConfig = hasPermission(user, 'ADMIN_CONFIG');

  const [status, setStatus] = useState<string>('');
  const [rows, setRows] = useState<BankTransaction[] | null>(null);
  const [meta, setMeta] = useState<BankMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);         // import pliku
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki
  const [syncing, setSyncing] = useState(false);   // synchronizacja Erste
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = () => api.transactions((status || undefined) as BankTxMatchStatus | undefined)
    .then(setRows).catch(e => setErr(e?.message ?? String(e)));
  const refreshMeta = () => api.meta().then(setMeta).catch(() => {});
  useEffect(() => { setRows(null); setErr(null); reload(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { refreshMeta(); }, []);

  const syncErste = () => {
    setSyncing(true); setErr(null);
    api.sync(14).then(r => {
      setSyncing(false);
      if (meta?.ersteMode === 'off') {
        alert('Synchronizacja pominięta — integracja Erste jest wyłączona (tryb „off"). Włącz ją w Ustawieniach Erste.');
      } else {
        alert(`Synchronizacja Erste: ${r.imported} nowych transakcji, ${r.matched} dopasowań, ${r.skipped} pominiętych.`);
      }
      reload(); refreshMeta();
    }).catch(e => { setSyncing(false); setErr(e?.message ?? String(e)); refreshMeta(); });
  };

  // „Ostatnia synchronizacja" = ostatnie UDANE pobranie; nieudaną/pominiętą próbę pokazujemy osobno.
  const ls = meta?.lastSync ?? null;
  const successAt = fmtDateTime(ls?.successAt);
  const attemptAt = fmtDateTime(ls?.at);
  const lastFailed = ls?.status === 'ERROR';
  const attemptNote = lastFailed
    ? `Ostatnia próba nieudana${attemptAt ? ` (${attemptAt})` : ''}`
    : ls?.status === 'SKIPPED'
      ? `Pominięto — Erste wyłączony${attemptAt ? ` (${attemptAt})` : ''}`
      : null;

  const upload = (f: File | undefined) => {
    if (!f) return;
    // limit rozmiaru pliku (100 MB) — blokujemy przed wysyłką
    const sizeErr = validateUploadSize(f);
    if (sizeErr) { setErr(sizeErr); if (fileRef.current) fileRef.current.value = ''; return; }
    setBusy(true); setErr(null); setUploadPct(0);
    api.importStatement(f, (p: UploadProgress) => setUploadPct(p.percent))
      .then(r => { setBusy(false); alert(`Import „${f.name}": ${r.imported} nowych, ${r.matched} dopasowań, ${r.skipped} pominiętych.`); reload(); })
      .catch(e => { setBusy(false); setErr(e?.message ?? String(e)); })
      .finally(() => { setUploadPct(null); if (fileRef.current) fileRef.current.value = ''; });
  };

  const confirm = (tx: BankTransaction) => {
    if (!tx.matchedInvoiceId) return;
    api.confirm(tx.id, tx.matchedInvoiceId).then(reload).catch(e => alert(e?.message ?? String(e)));
  };
  const reject = (tx: BankTransaction) => {
    api.reject(tx.id).then(reload).catch(e => alert(e?.message ?? String(e)));
  };

  const STATUSES: { id: string; name: string }[] = [
    { id: '', name: 'Wszystkie' },
    { id: 'SUGGESTED', name: 'Sugerowane dopasowanie' },
    { id: 'UNMATCHED', name: 'Niedopasowane' },
    { id: 'CONFIRMED', name: 'Potwierdzone' },
    { id: 'IGNORED', name: 'Pominięte' },
  ];

  return (
    <div className="max-w-[1300px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Bank / rozliczenia</h1>
          <div className="text-ink-400 text-[13px]">
            Transakcje z Erste Bank i wyciągów, dopasowane do faktur
            {meta && <span className="ml-1 pill pill-earth text-[10px]">Erste: {meta.ersteMode}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right leading-tight mr-1" title="Synchronizacja automatyczna (co 4 h) oraz ręcznie przyciskiem">
            <div className="text-ink-400 text-[10px] uppercase tracking-wide">Ostatnia synchronizacja</div>
            {successAt ? (
              <div className="text-[11px] text-ink-600 whitespace-nowrap">
                <span className="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle bg-green-500"/>
                {successAt}
                {ls?.status === 'OK' && (
                  <span className="text-ink-400"> · {ls.trigger === 'MANUAL' ? 'ręczna' : 'automatyczna'}</span>
                )}
              </div>
            ) : (
              <div className="text-[11px] text-ink-400">jeszcze nie zsynchronizowano</div>
            )}
            {attemptNote && (
              <div className={'text-[11px] whitespace-nowrap ' + (lastFailed ? 'text-red-500' : 'text-ink-400')}
                title={ls?.message ?? undefined}>
                ⚠ {attemptNote}
              </div>
            )}
          </div>
          {canConfig && (
            <Link to="/admin/konfiguracja?tab=ERSTE" className="btn btn-secondary btn-sm" title="Konfiguracja Erste Bank w panelu administracyjnym">
              <Icon name="settings" className="w-3.5 h-3.5"/> Ustawienia Erste
            </Link>
          )}
          <button disabled={busy || syncing} onClick={syncErste} className="btn btn-primary btn-sm">
            <Icon name="cloud" className="w-3.5 h-3.5"/> {syncing ? 'Synchronizuję…' : 'Synchronizuj z Erste'}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.mt940,.sta,.txt" className="hidden" onChange={e => upload(e.target.files?.[0])}/>
          <button disabled={busy || syncing} onClick={() => fileRef.current?.click()} className="btn btn-secondary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/> {busy ? 'Import…' : 'Wgraj wyciąg'}
          </button>
          {uploadPct !== null && (
            <UploadProgressBar percent={uploadPct} label="Wysyłanie wyciągu…" compact className="w-40"/>
          )}
        </div>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}

      <div className="card">
        <div className="card-head">
          <div className="w-64">
            <SearchableSelect value={status} onChange={setStatus}>
              {STATUSES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SearchableSelect>
          </div>
        </div>
        <div className="card-body !p-0">
          {rows == null && <div className="p-4 text-ink-400">Ładowanie…</div>}
          {rows && rows.length === 0 && <div className="p-6 text-center text-ink-400 text-[12px]">Brak transakcji. Wgraj wyciąg bankowy.</div>}
          {rows && rows.length > 0 && (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Data</th><th className="text-right">Kwota</th><th>Kontrahent</th><th>Tytuł</th>
                  <th>Dopasowanie</th><th>Faktura</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(tx => (
                  <tr key={tx.id} className="hover:bg-ink-50">
                    <td className="font-mono text-[12px] text-ink-500">{tx.bookingDate}</td>
                    <td className={'text-right font-mono text-[13px] ' + (tx.amount < 0 ? 'text-red-600' : 'text-green-700')}>{formatCurrency(tx.amount)}</td>
                    {/* max-width działa dopiero na bloku w komórce — na <td> jest ignorowane (table-layout:auto) */}
                    <td className="text-[13px] text-ink-800">
                      <div className="truncate max-w-[200px]" title={tx.counterpartyName ?? ''}>{tx.counterpartyName ?? '—'}</div>
                    </td>
                    <td className="text-[12px] text-ink-500">
                      <div className="truncate max-w-[240px]" title={tx.title ?? ''}>{tx.title ?? '—'}</div>
                    </td>
                    <td><span className={'pill pill-' + tx.matchStatusColor}>{tx.matchStatusName}</span></td>
                    <td className="text-[12px] text-ink-700">
                      {tx.matchedInvoiceNumber
                        ? <span className="font-mono">{tx.matchedInvoiceNumber} <span className="text-ink-400">({formatCurrency(tx.matchedInvoiceGross)})</span></span>
                        : '—'}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {tx.matchStatus === 'SUGGESTED' && (
                        <>
                          <button onClick={() => confirm(tx)} className="btn btn-ghost btn-icon text-green-600 hover:bg-green-50" title="Potwierdź dopasowanie"><Icon name="check" className="w-3.5 h-3.5"/></button>
                          <button onClick={() => reject(tx)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500" title="Odrzuć (nie dotyczy faktur)"><Icon name="x" className="w-3.5 h-3.5"/></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
